import { type CreateParticipantKeyContactRequest, LabelType } from '@piccolohealth/pbs-common';
import { ChakraV3, DndKitCore, DndKitSortable, DragHandle, Sortable } from '@piccolohealth/ui';
import { uuid } from '@piccolohealth/util';
import React from 'react';
import { useFieldArray } from 'react-hook-form';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { FormStack } from './FormStack';
import { HookedFormField } from './HookedFormField';
import { HookedInput } from './HookedInput';
import { HookedLabelChooser } from './HookedLabelChooser';

interface KeyContactItemProps {
  contact: CreateParticipantKeyContactRequest;
  name: string;
  isDisabled?: boolean;
  onRemove: () => void;
}

const KeyContactItem = (props: KeyContactItemProps) => {
  const { contact, name, isDisabled, onRemove } = props;

  const { attributes, setNodeRef, listeners, transform, transition } = DndKitSortable.useSortable({
    id: contact.id,
    disabled: isDisabled,
  });

  return (
    <Sortable ref={setNodeRef} transform={transform} transition={transition}>
      <FormStack orientation='horizontal' key={`contact-${contact.id}`} alignItems='center'>
        <DragHandle listeners={listeners} attributes={attributes} size='xs' />
        <FormStack gap={4} pl={4} ml={1} borderLeftWidth='2px' borderColor='purple.400'>
          <FormStack orientation='horizontal'>
            <HookedFormField
              name={`${name}.name`}
              label='Name'
              helperText='The contacts name'
              disabled={isDisabled}
            >
              <HookedInput name={`${name}.name`} size='sm' placeholder='Enter a name' />
            </HookedFormField>
            <HookedFormField
              name={`${name}.email`}
              label='Email'
              helperText='The contacts email'
              disabled={isDisabled}
            >
              <HookedInput name={`${name}.email`} size='sm' placeholder='Enter an email ' />
            </HookedFormField>
          </FormStack>
          <FormStack orientation='horizontal'>
            <HookedFormField
              name={`${name}.phone`}
              label='Phone'
              helperText='The contacts phone'
              disabled={isDisabled}
            >
              <HookedInput name={`${name}.phone`} size='sm' placeholder='Enter a phone number' />
            </HookedFormField>
            <HookedFormField
              name={`${name}.description`}
              label='Description/relationship'
              helperText='Who is the contact?'
              disabled={isDisabled}
            >
              <HookedInput
                name={`${name}.description`}
                size='sm'
                placeholder='Enter a description or relationship'
              />
            </HookedFormField>
          </FormStack>
          <FormStack orientation='horizontal'>
            <HookedFormField name={`${name}.labelIds`} label='Labels' disabled={isDisabled}>
              <HookedLabelChooser
                name={`${name}.labelIds`}
                type={LabelType.ParticipantKeyContact}
                size='sm'
                isDisabled={isDisabled}
              />
            </HookedFormField>
          </FormStack>
        </FormStack>
        <ChakraV3.IconButton
          aria-label='Remove key contact'
          size='2xs'
          variant='ghost'
          disabled={isDisabled}
          onClick={onRemove}
        >
          <FaTrash />
        </ChakraV3.IconButton>
      </FormStack>
    </Sortable>
  );
};

interface Props {
  name: string;
  isDisabled?: boolean;
}

export const HookedKeyContactsControl = (props: Props) => {
  const { name, isDisabled } = props;

  const { fields, append, remove, move } = useFieldArray({
    name,
  });

  const sensors = DndKitCore.useSensors(
    DndKitCore.useSensor(DndKitCore.PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const contacts = fields as unknown as CreateParticipantKeyContactRequest[];

  const onAdd = React.useCallback(() => {
    append({
      id: uuid(),
      name: null,
      phone: null,
      email: null,
      description: null,
      labelIds: [],
    });
  }, [append]);

  const onRemove = React.useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  const onDragEnd = React.useCallback(
    async (event: DndKitCore.DragEndEvent) => {
      const { active, over } = event;

      if (!active || !over) {
        return;
      }

      const oldIndex = contacts.findIndex((item) => item.id === active.id);
      const newIndex = contacts.findIndex((item) => item.id === over.id);

      if (oldIndex === newIndex) {
        return;
      }

      move(oldIndex, newIndex);
    },
    [contacts, move],
  );

  return (
    <FormStack>
      <DndKitCore.DndContext
        sensors={sensors}
        collisionDetection={DndKitCore.closestCenter}
        onDragEnd={onDragEnd}
      >
        <DndKitSortable.SortableContext
          strategy={DndKitSortable.verticalListSortingStrategy}
          items={contacts.map(({ id }) => id)}
        >
          {contacts.map((contact, index) => {
            return (
              <KeyContactItem
                key={contact.id}
                contact={contact}
                name={`${name}.${index}`}
                isDisabled={isDisabled}
                onRemove={() => onRemove(index)}
              />
            );
          })}
        </DndKitSortable.SortableContext>
      </DndKitCore.DndContext>
      <ChakraV3.Button variant='subtle' size='sm' disabled={isDisabled} onClick={onAdd}>
        <FaPlus />
        Add a contact
      </ChakraV3.Button>
    </FormStack>
  );
};
